import { Calendar, AlertTriangle, Clock, AlertCircle } from 'lucide-react';
import type { AgedStockStats as StatsType, AgedStockFlagStats, AgedStockFilters } from '../../types/agedStock';

interface AgedStockStatsProps {
    stats: StatsType | null;
    flagStats: AgedStockFlagStats | null;
    total: number;
    onApplyFilter?: (patch: Partial<AgedStockFilters>) => void;
    onFlagTileClick?: (flagType: 'Expired' | 'QualityIssue') => void;
}

function formatEUR(value: number): string {
    return new Intl.NumberFormat('de-CH', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

function formatCount(value: number): string {
    return value.toLocaleString('de-CH');
}

export function AgedStockStatsView({ stats, flagStats, total, onApplyFilter, onFlagTileClick }: AgedStockStatsProps) {
    return (
        <>
            {/* Total Lots — informational */}
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                <p className="text-xs font-medium text-gray-500">Total Lots</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">{formatCount(total)}</p>
            </div>

            {/* Stock Value — informational */}
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                <p className="text-xs font-medium text-gray-500">Stock Value</p>
                <p className="mt-1 text-lg font-semibold text-blue-600">
                    {stats ? formatEUR(stats.totalStockValueEUR) : '-'}
                </p>
            </div>

            {/* Lots > 1 Year — clickable: filter to minAgeDays = 366 */}
            <div
                className="rounded-lg border border-gray-200 bg-white px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => onApplyFilter?.({
                    minAgeDays: 366,
                    maxAgeDays: undefined,
                    ageBucket: undefined,
                    expiryStatus: undefined,
                })}
                title="Click to filter list to lots older than 1 year"
            >
                <p className="text-xs font-medium text-gray-500">Lots &gt; 1 Year</p>
                <p className="mt-1 text-lg font-semibold text-orange-600">
                    {stats
                        ? `${formatCount(stats.lotsOver1Year)} / ${formatEUR(stats.lotsOver1YearValueEUR)}`
                        : '-'}
                </p>
            </div>

            {/* Expiring < 90 days — clickable: filter expiryStatus = expiringSoon */}
            <div
                className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 cursor-pointer hover:bg-yellow-100 transition-colors"
                onClick={() => onApplyFilter?.({
                    expiryStatus: 'expiringSoon',
                    ageBucket: undefined,
                    minAgeDays: undefined,
                    maxAgeDays: undefined,
                })}
                title="Click to filter list to lots expiring in less than 90 days"
            >
                <p className="flex items-center gap-1 text-xs font-medium text-yellow-700">
                    <Clock className="h-3.5 w-3.5" /> Expiring &lt; 90 days
                </p>
                <p className="mt-1 text-lg font-semibold text-yellow-800">
                    {stats ? formatCount(stats.expiringSoonLots) : '-'}
                </p>
            </div>

            {/* Expired (system) — clickable: filter expiryStatus = expired */}
            <div
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => onApplyFilter?.({
                    expiryStatus: 'expired',
                    ageBucket: undefined,
                    minAgeDays: undefined,
                    maxAgeDays: undefined,
                })}
                title="Click to filter list to expired lots"
            >
                <p className="flex items-center gap-1 text-xs font-medium text-red-600">
                    <Calendar className="h-3.5 w-3.5" /> Expired (system)
                </p>
                <p className="mt-1 text-lg font-semibold text-red-700">
                    {stats ? `${formatCount(stats.expiredLots)} / ${formatEUR(stats.expiredValueEUR)}` : '-'}
                </p>
            </div>

            {/* Flagged: Expired — clickable: opens flagged-items modal */}
            <div
                className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => onFlagTileClick?.('Expired')}
                title="Click to view all lots flagged as Expired"
            >
                <p className="flex items-center gap-1 text-xs font-medium text-red-700">
                    <AlertTriangle className="h-3.5 w-3.5" /> Flagged: Expired
                </p>
                <p className="mt-1 text-lg font-semibold text-red-800">
                    {flagStats
                        ? `${formatCount(flagStats.byType.Expired.count)} / ${formatEUR(flagStats.byType.Expired.valueEUR)}`
                        : '-'}
                </p>
            </div>

            {/* Flagged: Quality Issue — clickable: opens flagged-items modal */}
            <div
                className="rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 cursor-pointer hover:bg-purple-100 transition-colors"
                onClick={() => onFlagTileClick?.('QualityIssue')}
                title="Click to view all lots flagged with Quality Issue"
            >
                <p className="flex items-center gap-1 text-xs font-medium text-purple-700">
                    <AlertCircle className="h-3.5 w-3.5" /> Flagged: Quality
                </p>
                <p className="mt-1 text-lg font-semibold text-purple-800">
                    {flagStats
                        ? `${formatCount(flagStats.byType.QualityIssue.count)} / ${formatEUR(flagStats.byType.QualityIssue.valueEUR)}`
                        : '-'}
                </p>
            </div>
        </>
    );
}
